import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  HttpErrorResponse
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  NO_ERRORS_SCHEMA,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  Injector
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Amplify, Auth } from 'aws-amplify';
import { NgxFileDropModule } from 'ngx-file-drop';
import { NgxSpinnerModule } from 'ngx-spinner';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomCelerityComponents } from './components/custom/custom.module';
import { ToastsContainerComponent } from './components/custom/messaging/toast/toast.component';
import { TopNavComponent } from './components/custom/top-nav/top-nav.component';
import { AppointmentChangeComponent } from './components/modals/appointment-change/appointment-change.component';
import { AppointmentCreateComponent } from './components/modals/appointment-create/appointment-create.component';
import { AppointmentDetailsComponent } from './components/modals/appointment-details/appointment-details.component';
import { FileUploadComponent } from './components/modals/file-upload/file-upload.component';
import { FileUploadPatientComponent } from './components/modals/file-upload-patient/file-upload-patient.component';
import { MessageDialogComponent } from './components/modals/message-dialog/message-dialog.component';
import { AgencyDisabledComponent } from './components/routes/auth/agency-disabled/agency-disabled.component';
import { AuthLandingComponent } from './components/routes/auth/auth-landing/auth-landing.component';
import { ConfirmResetComponent } from './components/routes/auth/confirm-reset/confirm-reset.component';
import { RequestResetComponent } from './components/routes/auth/request-reset/request-reset.component';
import { SignupComponent } from './components/routes/auth/signup/signup.component';
import { JwtInterceptorService } from './services/jwt-interceptor.service';
import { PatientService } from './services/patient.service';
import { BasicAgency } from './services/rest-api.service';
import { PdfViewerComponent } from './components/modals/pdf-viewer/pdf-viewer.component';
import { ServerDownComponent } from './components/routes/auth/server-down/server-down.component';
import { AdminService } from './services/admin.service';
import { ProgramNameComponent } from './components/modals/program-name/program-name.component';
import { JotFormViewerComponent } from './components/modals/jot-form-viewer/jot-form-viewer.component';
import { PaymentFormComponent } from './components/modals/payment-form/payment-form.component';
import { PaymentConfirmationComponent } from './components/modals/payment-confirmation/payment-confirmation.component';
import { PaymentOptionsComponent } from './components/modals/payment-options/payment-options.component';

@NgModule({
  declarations: [
    AppComponent,
    AuthLandingComponent,
    SignupComponent,
    RequestResetComponent,
    ConfirmResetComponent,
    FileUploadComponent,
    FileUploadPatientComponent,
    ToastsContainerComponent,
    AppointmentCreateComponent,
    AppointmentDetailsComponent,
    AppointmentChangeComponent,
    MessageDialogComponent,
    AgencyDisabledComponent,
    PdfViewerComponent,
    JotFormViewerComponent,
    ServerDownComponent,
    ProgramNameComponent,
    PaymentFormComponent,
    PaymentConfirmationComponent,
    PaymentOptionsComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    NgbModule,
    FontAwesomeModule,
    FormsModule,
    CustomCelerityComponents,
    NgxSpinnerModule,
    NgxFileDropModule
  ],
  exports: [TopNavComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptorService,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCelerityApp,
      deps: [Router, PatientService, AdminService, HttpClient, Injector],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}

export function initializeCelerityApp(
  router: Router,
  patient: PatientService,
  admin: AdminService,
  http: HttpClient,
  injector: Injector
) {
  return (): Promise<any> => {
    prepareInactivityTimeout(patient, admin, router, injector);
    return setDefaultRoute(router, patient, http);
  };
}

function prepareInactivityTimeout(
  patient: PatientService,
  admin: AdminService,
  router: Router,
  injector: Injector
) {
  let lastAction = new Date().getTime();
  let modals;
  document.onclick = () => {
    lastAction = new Date().getTime();
  };
  setInterval(() => {
    const now = new Date().getTime();
    if (now - lastAction > 1000 * 60 * 30) {
      // 30 minutes
      lastAction = now;
      if (patient.currentPatient) {
        console.log('Inactivity timeout');
        if (!modals) modals = injector.get(NgbModal);
        modals.dismissAll('Dialog dismissed due to inactivity');
        Auth.signOut().then(() => {
          delete admin.actAsPatientId;
          patient.currentPatient = null;
          return router.navigateByUrl('/auth/landing');
        });
      }
    }
  }, 1000);
}

function calculateAgencySlug(): string {
  let hostname = location.hostname;
  hostname = hostname.substr(0, hostname.indexOf('.'));
  if (hostname === '') {
    hostname = 'waybackinn';
  }
  console.log('Agency slug: ' + hostname);
  return hostname;
}

async function setDefaultRoute(
  router: Router,
  patient: PatientService,
  http: HttpClient
) {
  try {
    const settings = await http.get<any>('/api/public/settings').toPromise();
    Amplify.configure(settings);
    patient.currentAgency = await http
      .get<BasicAgency>('/api/public/agency/' + calculateAgencySlug())
      .toPromise();
  } catch (e) {
    if (e instanceof HttpErrorResponse) {
      if ([502, 503, 504].includes((e as HttpErrorResponse).status)) {
        return router.navigateByUrl('/auth/server-down');
      }
    }
  }
  if (
    !/^#\/Signup\/[A-Za-z0-9]+$/.test(location.hash) &&
    !/^#\/PasswordReset\/[A-Za-z0-9]+$/.test(location.hash) &&
    location.hash !== '#/auth/landing'
  ) {
    return router.navigateByUrl('/auth/landing');
  }
  return Promise.resolve(true);
}
